export interface User {
  id: string;
  family_id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Member';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Family {
  id: string;
  name: string;
  address?: string;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  role?: 'Admin' | 'Member';
}

export interface UpdateFamilyRequest {
  name?: string;
  address?: string;
  currency?: string;
}

export type PaymentType = 'bank' | 'cash';
export type IncomeType = 'bank' | 'cash';

export interface Expense {
  id: string;
  user_id: string;
  amount: number;
  category: string;
  description: string;
  payment_type: PaymentType;
  date: string;
  created_at: string;
  tags?: string[];
}

export interface CreateExpenseRequest {
  user_id: string;
  amount: number;
  category: string;
  description: string;
  payment_type: PaymentType;
  date?: string;
  tags?: string[];
}

export interface UpdateExpenseRequest {
  amount?: number;
  category?: string;
  description?: string;
  payment_type?: PaymentType;
  date?: string;
  tags?: string[];
}

export interface Income {
  id: string;
  user_id: string;
  amount: number;
  income_type: IncomeType;
  description: string;
  date: string;
  created_at: string;
}

export interface CreateIncomeRequest {
  amount: number;
  income_type: IncomeType;
  description: string;
  date?: string;
}

export interface UpdateIncomeRequest {
  amount?: number;
  income_type?: IncomeType;
  description?: string;
  date?: string;
}

export interface ExpenseAnalytics {
  total_amount: number;
  expense_count: number;
  categories: CategorySummary[];
  daily_totals: DailySummary[];
  monthly_totals: MonthlySummary[];
}

export interface CategorySummary {
  category: string;
  total_amount: number;
  count: number;
}

export interface DailySummary {
  date: string;
  total_amount: number;
  count: number;
}

export interface MonthlySummary {
  month: string;
  total_amount: number;
  count: number;
}

export interface IncomeAnalytics {
  total_amount: number;
  income_count: number;
  income_types: IncomeTypeSummary[];
  daily_totals: DailySummary[];
  monthly_totals: MonthlySummary[];
}

export interface IncomeTypeSummary {
  income_type: string;
  total_amount: number;
  count: number;
}

export interface FinancialSummary {
  total_income: number;
  total_expenses: number;
  remaining_balance: number;
  bank_income: number;
  cash_income: number;
  bank_expenses: number;
  cash_expenses: number;
  bank_balance: number;
  cash_balance: number;
}

export type ViewType = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'date-range';

export const EXPENSE_CATEGORIES = [
  'Food',
  'Transportation',
  'Shopping',
  'Entertainment',
  'Utilities',
  'Healthcare',
  'Education',
  'Travel',
  'Housing',
  'Insurance',
  'Other'
] as const;

export type ExpenseCategory = typeof EXPENSE_CATEGORIES[number];

// Currency utilities
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
};

export const formatCurrency = (amount: number, currencyCode: string): string => {
  const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;

  // Format number with proper decimal places
  const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // For currencies that typically go after the amount
  if (['EUR'].includes(currencyCode)) {
    return `${formattedAmount} ${symbol}`;
  }

  // For most currencies, symbol goes before
  return `${symbol}${formattedAmount}`;
};

export const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};
